import React, { useState, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { VideoPlayer } from './VideoPlayer';
import { useRequestVideoUrl } from '~/requests/videoOnDemandService/useRequestVideoUrl';
import { makeStyles } from '~/UIKit/Style';
import axios from 'axios';

const useStyles = makeStyles({
  videoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'flex-start',
  },
  thumbnail: {
    width: 100,
    height: 'auto',
    objectFit: 'contain',
  },
});

interface VoDPreviewProps {
  isEdit?: boolean;
  onSaveThumbnail?: (position: number) => void;
  error?: string;
  videoId?: string;
  thumbnailUrl?: string;
}

export const VoDPreview: React.FC<VoDPreviewProps> = ({
  isEdit = false,
  onSaveThumbnail,
  error,
  videoId,
  thumbnailUrl,
}) => {
  // eslint-disable-next-line import/no-named-as-default-member
  const cancelRequests = axios.CancelToken.source();

  const { t } = useTranslation();
  const [shouldShowButton, setShouldShowButton] = useState(false);
  const [thumbnailPosition, setThumbnailPosition] = useState<number>(0);
  const [videoSource, setVideoSource] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { classes } = useStyles();

  const { getVideoUrl, videoUrlResponse, videoUrlError } = useRequestVideoUrl(cancelRequests.token);

  useEffect(() => {
    if (videoId) {
      setIsLoading(true);
      getVideoUrl({ videoId });
    }
    return () => {
      cancelRequests.cancel();
    };
  }, [videoId]);

  useEffect(() => {
    if (videoUrlResponse?.url) {
      setVideoSource(videoUrlResponse?.url);
      setIsLoading(false);
    }
  }, [videoUrlResponse?.url]);

  useEffect(() => {
    if (videoUrlError?.message) {
      setIsLoading(false);
    }
  }, [videoUrlError?.message]);

  const handleSaveThumbnail = () => {
    onSaveThumbnail?.(thumbnailPosition);
  };

  const errorResponse = error || videoUrlError?.message;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        width: '100%',
        padding: 2,
      }}
    >
      {isLoading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : errorResponse ? (
        <Typography color="error">{errorResponse}</Typography>
      ) : (
        <Box>
          <div className={classes.videoContainer}>
            <VideoPlayer
              videoJsOptions={{
                autoplay: false,
                playbackRates: [0.5, 1, 1.25, 1.5, 2],
                width: 500,
                height: 300,
                controls: true,
                sources: [
                  {
                    src: videoSource,
                    type: 'application/x-mpegURL',
                  },
                ],
              }}
              isEdit={isEdit}
              setThumbnailPosition={setThumbnailPosition}
              setShouldShowButton={setShouldShowButton}
            />
            {thumbnailUrl && <img className={classes.thumbnail} src={thumbnailUrl} />}
          </div>
          {isEdit && shouldShowButton && (
            <Button variant="contained" onClick={handleSaveThumbnail} sx={{ mt: 2 }}>
              {t('app.vod.saveThumbnail')}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
